import React, { useEffect, useRef } from 'react';
import { useT } from '@/i18n/labels';

interface ReturnToTeamDialogProps {
  /** Who is asking: the owner giving the conversation up, or a tenant admin returning someone else's. */
  mode: 'mine' | 'admin';
  /** The owner's display name, for the admin wording. Never an id. */
  ownerName: string;
  /** True while the invoke is in flight: both buttons and Escape are inert so it cannot be sent twice. */
  busy: boolean;
  /** The refusal / failure notice for the last attempt, if any. */
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ADR-033 - the confirm for "Return to team". It says the consequence in one sentence: the conversation becomes
 * visible to the whole team.
 *
 * Built into the page, NOT `window.confirm` (the host blocks native dialogs, and a blocked confirm() silently makes the
 * action impossible - same reason as SpamConfirmDialog). Default focus is Cancel; nothing is sent until Confirm is
 * pressed; Tab stays inside. Rendered inline so it inherits the root `dir`; logical properties only.
 */
export const ReturnToTeamDialog: React.FC<ReturnToTeamDialogProps> = ({
  mode,
  ownerName,
  busy,
  error,
  onConfirm,
  onCancel,
}) => {
  const { t } = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      if (!busy) onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="ac-modal-backdrop" data-testid="return-team-backdrop">
      <div
        ref={dialogRef}
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="return-team-title"
        aria-describedby="return-team-body"
        data-testid="return-team-dialog"
        onKeyDown={onKeyDown}
      >
        <h2 id="return-team-title" className="ac-modal-title">
          {mode === 'admin' ? t('btn_return_team_admin') : t('btn_return_team')}
        </h2>
        <p id="return-team-body" className="ac-modal-body" dir="auto">
          {mode === 'admin' ? t('return_team_confirm_admin', { name: ownerName }) : t('return_team_confirm_mine')}
        </p>

        {error && (
          <div role="alert" className="ac-modal-error" data-testid="return-team-error">
            {error}
          </div>
        )}

        <div className="ac-modal-actions">
          <button
            ref={cancelRef}
            type="button"
            className="ac-btn ghost sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="return-team-cancel"
          >
            {t('return_team_cancel')}
          </button>
          <button
            type="button"
            className="ac-btn primary sm"
            onClick={onConfirm}
            disabled={busy}
            data-testid="return-team-confirm"
          >
            {busy ? t('return_team_working') : t('btn_return_team')}
          </button>
        </div>
      </div>
    </div>
  );
};
